import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTimezone } from "../contexts/TimezoneContext";
import { formatInOrgTimezone, isDateInPastInOrgTimezone } from "../utils/timezoneUtils";
import { 
  addToGoogleCalendar, 
  addToOutlookCalendar, 
  addToYahooCalendar, 
  addToAppleCalendar, 
  downloadICSFile 
} from "../utils/calendarUtils";

function EditBookingModal({ 
  open, 
  onClose, 
  booking, 
  onSave, 
  availableSlots = [],
  organizationWorkHours = { start: "08:00", end: "18:00" }
}) {
  const { orgTimeZone } = useTimezone();
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [meetingName, setMeetingName] = useState("");
  const [loading, setLoading] = useState(false);
  const [originalDuration, setOriginalDuration] = useState(null); // Track original duration in minutes
  const [showCalendarOptions, setShowCalendarOptions] = useState(false); // Show calendar integration after save

  // Initialize form with current booking times
  useEffect(() => {
    if (booking && orgTimeZone) {
      // Convert booking times to organization timezone for editing
      const startInOrgTz = moment.tz(booking.startTime, orgTimeZone);
      const endInOrgTz = moment.tz(booking.endTime, orgTimeZone);
      
      setStartTime(startInOrgTz.toDate());
      setEndTime(endInOrgTz.toDate());
      setMeetingName(booking.meetingName || "");
      
      // Calculate and store original duration in minutes
      const duration = endInOrgTz.diff(startInOrgTz, 'minutes');
      setOriginalDuration(duration);
    }
  }, [booking, orgTimeZone]);

  const handleSave = async () => {
    if (!startTime || !endTime) {
      return;
    }

    if (endTime <= startTime) {
      alert("End time must be after start time");
      return;
    }

    // Check if the booking is in the past
    if (isDateInPastInOrgTimezone(startTime, orgTimeZone)) {
      alert("Cannot edit past bookings");
      return;
    }

    // Check if the new time is within organization work hours
    const startMoment = moment.tz(startTime, orgTimeZone);
    const endMoment = moment.tz(endTime, orgTimeZone);
    
    const [orgStartHour, orgStartMin] = organizationWorkHours.start.split(":").map(Number);
    const [orgEndHour, orgEndMin] = organizationWorkHours.end.split(":").map(Number);
    
    const dayStart = startMoment.clone().set({ hour: orgStartHour, minute: orgStartMin, second: 0 });
    const dayEnd = startMoment.clone().set({ hour: orgEndHour, minute: orgEndMin, second: 0 });
    
    if (startMoment.isBefore(dayStart) || endMoment.isAfter(dayEnd)) {
      alert(`Time must be within organization's work hours: ${organizationWorkHours.start} - ${organizationWorkHours.end}`);
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        meetingName: meetingName || 'Meeting',
      };
      await onSave(booking._id, updateData);
      // Show calendar options after successful save
      setShowCalendarOptions(true);
    } catch (error) {
      console.error("Error updating booking:", error);
      alert(error.message || "Failed to update booking");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    if (booking && orgTimeZone) {
      const startInOrgTz = moment.tz(booking.startTime, orgTimeZone);
      const endInOrgTz = moment.tz(booking.endTime, orgTimeZone);
      
      setStartTime(startInOrgTz.toDate());
      setEndTime(endInOrgTz.toDate());
      setMeetingName(booking.meetingName || "");
      
      // Reset original duration
      const duration = endInOrgTz.diff(startInOrgTz, 'minutes');
      setOriginalDuration(duration);
    }
    setShowCalendarOptions(false);
    onClose();
  };

  // Handle adding updated booking to Google Calendar
  const handleAddToGoogleCalendar = () => {
    if (booking && startTime && endTime) {
      const updatedBooking = {
        ...booking,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        meetingName: meetingName || 'Meeting',
      };
      try {
        addToGoogleCalendar(updatedBooking, orgTimeZone);
      } catch (error) {
        alert("Failed to open Google Calendar");
      }
    }
  };

  // Handle adding updated booking to Outlook Calendar
  const handleAddToOutlookCalendar = () => {
    if (booking && startTime && endTime) {
      const updatedBooking = {
        ...booking,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        meetingName: meetingName || 'Meeting',
      };
      try {
        addToOutlookCalendar(updatedBooking, orgTimeZone);
      } catch (error) {
        alert("Failed to open Outlook Calendar");
      }
    }
  };

  // Handle adding updated booking to Yahoo Calendar
  const handleAddToYahooCalendar = () => {
    if (booking && startTime && endTime) {
      const updatedBooking = {
        ...booking,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        meetingName: meetingName || 'Meeting',
      };
      try {
        addToYahooCalendar(updatedBooking, orgTimeZone);
      } catch (error) {
        alert("Failed to open Yahoo Calendar");
      }
    }
  };

  // Handle adding updated booking to Apple Calendar
  const handleAddToAppleCalendar = () => {
    if (booking && startTime && endTime) {
      const updatedBooking = {
        ...booking,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        meetingName: meetingName || 'Meeting',
      };
      try {
        addToAppleCalendar(updatedBooking, orgTimeZone);
      } catch (error) {
        alert("Failed to open Apple Calendar");
      }
    }
  };

  // Handle downloading ICS file for updated booking
  const handleDownloadICS = () => {
    if (booking && startTime && endTime) {
      const updatedBooking = {
        ...booking,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        meetingName: meetingName || 'Meeting',
      };
      try {
        downloadICSFile(updatedBooking, orgTimeZone);
      } catch (error) {
        alert("Failed to download calendar file");
      }
    }
  };

  // Handle final close after calendar integration
  const handleFinalClose = () => {
    setShowCalendarOptions(false);
    onClose();
  };

  // Generate all available time slots
  const generateAllTimeSlots = () => {
    const slots = [];
    const [startHour, startMin] = organizationWorkHours.start.split(":").map(Number);
    const [endHour, endMin] = organizationWorkHours.end.split(":").map(Number);
    
    let hour = startHour;
    let minute = startMin;
    
    while (hour < endHour || (hour === endHour && minute <= endMin)) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeString);
      
      minute += 30;
      if (minute >= 60) {
        hour += 1;
        minute = 0;
      }
      
      if (hour > endHour) break;
    }
    
    return slots;
  };

  // Generate quick select options: ±2 hours around current time
  const getStartTimeOptions = () => {
    const allSlots = generateAllTimeSlots();
    if (!startTime) return allSlots; // Show all if no start time selected
    
    const currentStartString = moment(startTime).format("HH:mm");
    const currentStartMoment = moment(currentStartString, "HH:mm");
    
    // Get slots within ±2 hours
    const twoHoursBefore = currentStartMoment.clone().subtract(2, 'hours');
    const twoHoursAfter = currentStartMoment.clone().add(2, 'hours');
    
    return allSlots.filter(slot => {
      const slotMoment = moment(slot, "HH:mm");
      return slotMoment.isSameOrAfter(twoHoursBefore) && 
             slotMoment.isSameOrBefore(twoHoursAfter) &&
             (!endTime || slot < moment(endTime).format("HH:mm")); // Still respect end time
    });
  };

  const getEndTimeOptions = () => {
    const allSlots = generateAllTimeSlots();
    if (!endTime) return allSlots; // Show all if no end time selected
    
    const currentEndString = moment(endTime).format("HH:mm");
    const currentEndMoment = moment(currentEndString, "HH:mm");
    
    // Get slots within ±2 hours
    const twoHoursBefore = currentEndMoment.clone().subtract(2, 'hours');
    const twoHoursAfter = currentEndMoment.clone().add(2, 'hours');
    
    return allSlots.filter(slot => {
      const slotMoment = moment(slot, "HH:mm");
      return slotMoment.isSameOrAfter(twoHoursBefore) && 
             slotMoment.isSameOrBefore(twoHoursAfter) &&
             (!startTime || slot > moment(startTime).format("HH:mm")); // Still respect start time
    });
  };

  const handleQuickTimeSelect = (timeString, isStart) => {
    if (!booking) return;
    
    const bookingDate = moment.tz(booking.startTime, orgTimeZone);
    const [hour, minute] = timeString.split(":").map(Number);
    const newTime = bookingDate.clone().set({ hour, minute, second: 0, millisecond: 0 });
    
    if (isStart) {
      setStartTime(newTime.toDate());
      
      // Auto-adjust end time to maintain the same duration
      if (originalDuration && originalDuration > 0) {
        const newEndTime = newTime.clone().add(originalDuration, 'minutes');
        
        // Check if new end time is within work hours
        const [orgEndHour, orgEndMin] = organizationWorkHours.end.split(":").map(Number);
        const dayEnd = newTime.clone().set({ hour: orgEndHour, minute: orgEndMin, second: 0 });
        
        if (newEndTime.isSameOrBefore(dayEnd)) {
          setEndTime(newEndTime.toDate());
        } else {
          // If it would go beyond work hours, just set it to work hours end
          setEndTime(dayEnd.toDate());
        }
      }
    } else {
      setEndTime(newTime.toDate());
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6">
            {showCalendarOptions ? "Add to Calendar" : "Edit Booking"}
          </Typography>
          {!showCalendarOptions && (
            <Chip 
              label={`Times in ${orgTimeZone}`} 
              size="small" 
              variant="outlined" 
              color="primary"
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {showCalendarOptions ? (
          // Calendar Integration Options
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                🎉 Booking Updated Successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Would you like to add the updated booking to your calendar?
              </Typography>
            </Box>

            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider', mb: 3 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Updated Booking Details:
              </Typography>
              <Typography variant="body2">
                <strong>Meeting:</strong> {meetingName || 'Meeting'}
              </Typography>
              <Typography variant="body2">
                <strong>Room:</strong> {booking.room?.name || "Unknown Room"}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatInOrgTimezone(booking.startTime, orgTimeZone, "MMM DD, YYYY")}
              </Typography>
              <Typography variant="body2">
                <strong>New Time:</strong> {startTime && endTime ? 
                  `${moment(startTime).format('h:mm A')} - ${moment(endTime).format('h:mm A')} (${orgTimeZone})` :
                  'Invalid time'
                }
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Choose your calendar:
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<span>📅</span>}
                onClick={handleAddToGoogleCalendar}
                sx={{ 
                  backgroundColor: '#4285f4',
                  '&:hover': { backgroundColor: '#3367d6' }
                }}
              >
                Google
              </Button>
              
              <Button
                variant="contained"
                startIcon={<span>📘</span>}
                onClick={handleAddToOutlookCalendar}
                sx={{ 
                  backgroundColor: '#0078d4',
                  '&:hover': { backgroundColor: '#106ebe' }
                }}
              >
                Outlook
              </Button>
              
              <Button
                variant="contained"
                startIcon={<span>🟣</span>}
                onClick={handleAddToYahooCalendar}
                sx={{ 
                  backgroundColor: '#6001d2',
                  '&:hover': { backgroundColor: '#5000bb' }
                }}
              >
                Yahoo
              </Button>
              
              <Button
                variant="contained"
                startIcon={<span>🍎</span>}
                onClick={handleAddToAppleCalendar}
                sx={{ 
                  backgroundColor: '#000000',
                  '&:hover': { backgroundColor: '#1a1a1a' }
                }}
              >
                Apple
              </Button>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<span>📄</span>}
              onClick={handleDownloadICS}
              size="large"
              fullWidth
            >
              Download .ics file (Universal)
            </Button>
          </>
        ) : (
          // Edit Form
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Room: {booking.room?.name || "Unknown Room"}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Date: {formatInOrgTimezone(booking.startTime, orgTimeZone, "MMM DD, YYYY")}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Current: {formatInOrgTimezone(booking.startTime, orgTimeZone, "HH:mm")} - {formatInOrgTimezone(booking.endTime, orgTimeZone, "HH:mm")}
              </Typography>
              {originalDuration && (
                <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
                  <Chip 
                    label={`Duration: ${Math.floor(originalDuration / 60)}h ${originalDuration % 60}m`} 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                  />
                  <Typography variant="caption" color="text.secondary">
                    (End time adjusts automatically)
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Meeting Name (optional)"
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)}
                placeholder="Enter meeting name..."
                variant="outlined"
                size="medium"
              />
            </Box>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Start Time</Typography>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={(newStartTime) => {
                  setStartTime(newStartTime);
                  
                  // Auto-adjust end time to maintain the same duration
                  if (newStartTime && originalDuration && originalDuration > 0) {
                    const startMoment = moment(newStartTime);
                    const newEndTime = startMoment.clone().add(originalDuration, 'minutes');
                    
                    // Check if new end time is within work hours
                    const [orgEndHour, orgEndMin] = organizationWorkHours.end.split(":").map(Number);
                    const dayEnd = startMoment.clone().set({ hour: orgEndHour, minute: orgEndMin, second: 0 });
                    
                    if (newEndTime.isSameOrBefore(dayEnd)) {
                      setEndTime(newEndTime.toDate());
                    } else {
                      // If it would go beyond work hours, just set it to work hours end
                      setEndTime(dayEnd.toDate());
                    }
                  }
                }}
                minutesStep={30}
                sx={{ width: "100%", mb: 2 }}
              />
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick Select:</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {getStartTimeOptions().length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No available start times (please adjust end time)
                  </Typography>
                ) : (
                  getStartTimeOptions().map((slot) => (
                    <Button
                      key={`start-${slot}`}
                      size="small"
                      variant={startTime && moment(startTime).format("HH:mm") === slot ? "contained" : "outlined"}
                      onClick={() => handleQuickTimeSelect(slot, true)}
                      sx={{ minWidth: 60 }}
                    >
                      {slot}
                    </Button>
                  ))
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>End Time</Typography>
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={setEndTime}
                minutesStep={30}
                sx={{ width: "100%", mb: 2 }}
              />
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick Select:</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {getEndTimeOptions().length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No available end times (please adjust start time)
                  </Typography>
                ) : (
                  getEndTimeOptions().map((slot) => (
                    <Button
                      key={`end-${slot}`}
                      size="small"
                      variant={endTime && moment(endTime).format("HH:mm") === slot ? "contained" : "outlined"}
                      onClick={() => handleQuickTimeSelect(slot, false)}
                      sx={{ minWidth: 60 }}
                    >
                      {slot}
                    </Button>
                  ))
                )}
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {startTime && endTime && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider" }}>
            <Typography variant="subtitle2" color="primary">
              New Booking Time:
            </Typography>
            <Typography variant="body1">
              {moment(startTime).format("HH:mm")} - {moment(endTime).format("HH:mm")} ({orgTimeZone})
            </Typography>
          </Box>
        )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        {showCalendarOptions ? (
          <>
            <Button onClick={handleFinalClose} variant="text">
              Skip
            </Button>
            <Button onClick={handleFinalClose} variant="contained">
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              disabled={loading || !startTime || !endTime}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default EditBookingModal; 